import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/design-system/card'
import { Button } from '@/design-system/button'
import { Badge } from '@/design-system/badge'
import {
  Users, ArrowRight, Shuffle, UserCheck, Target,
} from 'lucide-react'
import {
  TOTAL_CONSULTANTS, TOTAL_FACTORS, TOTAL_SEGMENTS,
  TOTAL_MICROPROFILES,
} from '@/consultant-study/factors'
import { factors } from '@/consultant-study/factors'
import { ScreePlot } from '@/methodology/scree-plot'

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const researchNumbers = [
  { value: TOTAL_CONSULTANTS, label: 'Consultants surveyed', note: 'February 2026' },
  { value: `${TOTAL_FACTORS}`, label: 'Behavioral factors', note: 'Extracted via factor analysis' },
  { value: TOTAL_SEGMENTS, label: 'Segments', note: 'Validated by 4 quality tests' },
  { value: TOTAL_MICROPROFILES, label: 'Microprofiles', note: 'Each with lead matching rules' },
]

const factorDetails = [
  { id: 'F1', name: 'Professional Expertise & Clarity', items: 'Q40, Q43, Q41, Q32, Q39, Q13, Q30', eigenvalue: 10.379, variance: '20.76%', domain: 'Self-assessed competence, adaptability' },
  { id: 'F2', name: 'Price & Cost Barrier Perception', items: 'Q20, Q19', eigenvalue: 3.003, variance: '6.01%', domain: 'Beliefs about price/cost as obstacles' },
  { id: 'F3', name: 'Digital Technology Comfort', items: 'Q36, Q37, Q45, Q38(–), Q35, Q34', eigenvalue: 2.811, variance: '5.62%', domain: 'Digital tools, online orientation' },
  { id: 'F4', name: 'Client Orientation & Trust Building', items: 'Q7, Q16, Q15, Q10, Q17', eigenvalue: 1.927, variance: '3.85%', domain: 'Trust, patience, client-centricity' },
  { id: 'F5', name: 'Stress & Autonomy Orientation', items: 'Q50, Q49', eigenvalue: 1.757, variance: '3.51%', domain: 'Stress perception, autonomy preference' },
  { id: 'F6', name: 'Relational & Personal Sales Style', items: 'Q47, Q48, Q42', eigenvalue: 1.618, variance: '3.24%', domain: 'Personal connection, rapport, hobby discussions' },
  { id: 'F7', name: 'Perceived Product Complexity', items: 'Q31, Q33', eigenvalue: 1.507, variance: '3.01%', domain: 'Insurance complexity, simple techniques' },
  { id: 'F8', name: 'Results & Commission Motivation', items: 'Q5, Q9, Q6', eigenvalue: 1.410, variance: '2.82%', domain: 'Commission drive, target achievement, status' },
  { id: 'F9', name: 'Health System & Prevention Awareness', items: 'Q25, Q23, Q22, Q24', eigenvalue: 1.321, variance: '2.64%', domain: 'Health benefits, prevention, treatment access' },
  { id: 'F10', name: 'Social Influence Awareness', items: 'Q29, Q28, Q27', eigenvalue: 1.247, variance: '2.49%', domain: 'Peer influence, social pressure, family' },
  { id: 'F11', name: 'Assertive Closing & Value Demonstration', items: 'Q12, Q21', eigenvalue: 1.225, variance: '2.45%', domain: 'Firm guidance, value justification' },
  { id: 'F12', name: 'Direct Communication & Cost Orientation', items: 'Q46, Q44, Q11', eigenvalue: 1.135, variance: '2.27%', domain: 'Direct style, cost-sensitive clients' },
  { id: 'F13', name: 'Mobilization & Signal Reading', items: 'Q8, Q14', eigenvalue: 1.105, variance: '2.21%', domain: 'Self-mobilization, buying signal recognition' },
  { id: 'F14', name: 'Communication & Explanation Power', items: 'Q1, Q4, Q2, Q3', eigenvalue: 1.028, variance: '2.06%', domain: 'Impact of explanations, empathy, verification' },
]

const pipelineSteps = [
  {
    label: 'Survey',
    detail: '50 items, N=282',
    plain: 'We asked 282 consultants 50 questions about how they work, sell, and feel about their role.',
    color: 'border-t-blue-500',
  },
  {
    label: 'Factor Analysis',
    detail: '14 factors, 62.9% variance',
    plain: 'The algorithm found that those 50 answers can be explained by 14 underlying behavioral traits — like "client trust" or "closing assertiveness."',
    color: 'border-t-amber-500',
  },
  {
    label: 'Clustering',
    detail: 'K-Means, k=5',
    plain: 'We grouped consultants by their 14-trait fingerprint. 5 natural groups emerged — these are the segments.',
    color: 'border-t-emerald-500',
  },
  {
    label: 'Profiling',
    detail: '12 microprofiles',
    plain: 'Within each segment, we found 2-3 sub-types with distinct strengths and lead preferences.',
    color: 'border-t-violet-500',
  },
]

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function HomePage() {
  return (
    <div className="space-y-16 pb-16">

      {/* ============================================================ */}
      {/*  1. HERO                                                      */}
      {/* ============================================================ */}
      <section className="relative overflow-hidden border-b bg-gradient-to-br from-background via-background to-[#F17922]/5 px-6 py-20">
        <div className="mx-auto max-w-7xl">
          {/* Branding */}
          <div className="flex items-center gap-4 mb-8">
            <img src="/nn-logo.svg" alt="NN" className="h-14" />
            <div>
              <Badge className="bg-[#F17922] text-white">NN Insurance Consultant Study</Badge>
              <p className="text-xs text-gray-400 mt-1 uppercase tracking-widest">
                IGODEMY Research &amp; Behavioral Intelligence &middot; February 2026
              </p>
            </div>
          </div>

          {/* Headline */}
          <h1 className="max-w-4xl text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl">
            Same Leads.{' '}
            <span className="text-[#F17922]">Different Results.</span>
          </h1>

          <p className="mt-6 max-w-3xl text-lg text-muted-foreground leading-relaxed">
            Leads come in. They get assigned by geography or availability. But what do we actually
            know about the consultants receiving them? This study is the first step &mdash; a
            behavioral profiling of {TOTAL_CONSULTANTS} NN consultants to understand <em>how</em> they
            sell, <em>why</em> they sell, and who they sell best to.
          </p>

          {/* Today vs. with matching */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl">
            <div className="border border-dashed border-gray-300 bg-white p-5">
              <div className="flex items-center gap-2 mb-3">
                <Shuffle className="h-4 w-4 text-gray-400" />
                <span className="text-xs font-bold uppercase tracking-wider text-gray-400">Today</span>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                Leads assigned by geography or availability. No consideration of consultant
                behavioral strengths. An anxious first-time buyer might get an aggressive closer.
                58% of consultants convert below 10%.
              </p>
            </div>
            <div className="border border-[#F17922]/30 bg-[#F17922]/[0.02] p-5">
              <div className="flex items-center gap-2 mb-3">
                <UserCheck className="h-4 w-4 text-[#F17922]" />
                <span className="text-xs font-bold uppercase tracking-wider text-[#F17922]">With behavioral matching</span>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">
                Leads matched to the consultant whose behavioral profile fits the client&rsquo;s
                needs. Trust-builders get anxious buyers. Closers get decision-ready leads.
                Same lead volume, better outcomes.
              </p>
            </div>
          </div>

          {/* CTAs */}
          <div className="mt-10 flex gap-4">
            <Button asChild size="lg" className="bg-[#F17922] hover:bg-[#D96810]">
              <Link to="/profiles">
                Explore Profiles <ArrowRight className="ml-1 size-4" />
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline">
              <Link to="/matching">Matching Vision</Link>
            </Button>
          </div>
        </div>
        <div className="pointer-events-none absolute -right-32 -top-32 size-96 bg-[#F17922]/5 blur-3xl" />
      </section>

      {/* ============================================================ */}
      {/*  2. WHAT THE RESEARCH FOUND                                   */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="mb-10 max-w-3xl">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            What the Research Found
          </h2>
          <p className="mt-2 text-muted-foreground">
            Based on a 50-item behavioral survey of {TOTAL_CONSULTANTS} NN consultants,
            analyzed through factor analysis and cluster validation.
          </p>
        </div>

        {/* Research numbers */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
          {researchNumbers.map(m => (
            <div key={m.label} className="border border-gray-200 bg-white p-5">
              <div className="text-3xl font-bold text-[#F17922]">{m.value}</div>
              <div className="text-sm font-medium text-gray-900 mt-1">{m.label}</div>
              <div className="text-xs text-gray-400 mt-0.5">{m.note}</div>
            </div>
          ))}
        </div>

      </section>

      {/* ============================================================ */}
      {/*  3. METHODOLOGY — THE FOUR-STEP PIPELINE                     */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="mb-8 max-w-3xl">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Methodology
          </h2>
          <p className="mt-2 text-muted-foreground">
            How we built the profiling model — explained step by step.
          </p>
        </div>

        {/* Narrative intro */}
        <div className="mb-8 max-w-3xl">
          <Card className="border-l-4 border-l-[#8B5CF6] bg-[#8B5CF6]/[0.02]">
            <CardContent className="py-6 space-y-3">
              <p className="text-sm leading-relaxed text-gray-700">
                This section explains how we went from a 50-question survey to 5 consultant segments and
                12 actionable microprofiles. The short version: we collected behavioral data, used
                statistics to find hidden patterns, grouped similar consultants together, and validated
                that those groups are real — not random noise.
              </p>
              <p className="text-sm leading-relaxed text-gray-700">
                If you want the bottom line, read the plain-language summaries. If you want the math,
                the technical details are right alongside them.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Pipeline steps */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4">The Four-Step Pipeline</h3>
          <div className="flex flex-wrap items-stretch gap-3">
            {pipelineSteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-3">
                <Card className={`border-t-4 ${step.color} w-[220px]`}>
                  <CardHeader className="pb-1">
                    <CardTitle className="text-base">{step.label}</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <p className="text-xs font-mono text-muted-foreground">{step.detail}</p>
                    <p className="text-xs text-gray-600 leading-relaxed">{step.plain}</p>
                  </CardContent>
                </Card>
                {i < pipelineSteps.length - 1 && (
                  <ArrowRight className="size-5 shrink-0 text-muted-foreground" />
                )}
              </div>
            ))}
          </div>

          <div className="mt-4 border border-gray-200 bg-gray-50 p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-700">Why these specific techniques?</p>
            <p className="text-xs text-gray-600 leading-relaxed">
              <span className="font-medium">Factor Analysis</span> (specifically "Exploratory" / EFA)
              is used because we had no theory in advance about which traits matter — we let the data
              tell us. <span className="font-medium">K-Means clustering</span> groups consultants by
              similarity across all 14 traits at once — it works well for this data size (282 people,
              14 dimensions). We tried 3 through 8 groups and settled on <span className="font-medium">5
              segments</span> because that number scored best on the validation tests and
              produced groups that actually make sense operationally.
            </p>
          </div>
        </div>

        {/* Scree Plot */}
        <Card>
          <CardHeader>
            <div className="space-y-2">
              <CardTitle>How Many Traits Actually Matter?</CardTitle>
              <p className="text-sm text-muted-foreground leading-relaxed">
                The survey had 50 questions, but many of them measure the same underlying trait.
                Factor analysis collapses correlated questions into independent
                factors. The chart below shows how much each factor contributes.
                The rule of thumb (<span className="font-medium">Kaiser criterion</span>): keep every factor with
                an eigenvalue above 1.0 — meaning it explains more than a single survey
                question would on its own. Result: <span className="font-medium">14 factors</span> that
                together explain 62.9% of all behavioral variation.
              </p>
            </div>
          </CardHeader>
          <CardContent>
            <ScreePlot factors={factors} />
            <p className="mt-3 text-xs text-gray-500 leading-relaxed">
              Each dot is one factor. The dashed line at 1.0 is the cutoff. Everything above it
              carries meaningful signal; everything below would add noise. The steep drop from F1
              (Professional Expertise, eigenvalue 10.4) to F2 (3.0) shows that expertise
              is by far the strongest differentiator among consultants.
            </p>
          </CardContent>
        </Card>

        {/* 14 Factors table */}
        <div className="mt-10 mb-6">
          <h3 className="text-lg font-semibold mb-2">The 14 Behavioral Factors</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-3xl">
            Factor analysis of 50 survey items revealed 14 independent behavioral dimensions.
            Together they explain <span className="font-semibold text-gray-900">62.9%</span> of
            all variation in how consultants think, sell, and engage with clients.
            Every consultant has a unique fingerprint across these 14 traits.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm border border-gray-200">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="px-3 py-2.5 font-semibold text-gray-700 w-[52px]">Factor</th>
                <th className="px-3 py-2.5 font-semibold text-gray-700">Name</th>
                <th className="px-3 py-2.5 font-semibold text-gray-700 hidden lg:table-cell">Primary Items</th>
                <th className="px-3 py-2.5 font-semibold text-gray-700 text-right w-[90px]">Eigenvalue</th>
                <th className="px-3 py-2.5 font-semibold text-gray-700 text-right w-[90px]">Variance</th>
                <th className="px-3 py-2.5 font-semibold text-gray-700 hidden md:table-cell">Key Behavioral Domain</th>
              </tr>
            </thead>
            <tbody>
              {factorDetails.map((f, i) => (
                <tr key={f.id} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                  <td className="px-3 py-2 font-mono text-xs font-bold text-[#F17922]">{f.id}</td>
                  <td className="px-3 py-2 font-medium text-gray-900">{f.name}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-500 hidden lg:table-cell">{f.items}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-gray-700">{f.eigenvalue.toFixed(3)}</td>
                  <td className="px-3 py-2 text-right font-mono text-xs text-gray-700">{f.variance}</td>
                  <td className="px-3 py-2 text-xs text-gray-600 hidden md:table-cell">{f.domain}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-gray-100 border-t border-gray-300">
                <td colSpan={3} className="px-3 py-2 font-semibold text-gray-700 text-sm">Total (14 factors)</td>
                <td className="px-3 py-2 text-right font-mono text-xs font-bold text-gray-900 hidden lg:table-cell"></td>
                <td className="px-3 py-2 text-right font-mono text-xs font-bold text-[#F17922]">62.94%</td>
                <td className="hidden md:table-cell"></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  4. EXTENDED STRATEGIC VALUE                                  */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-7xl px-6">
        <div className="mb-8 max-w-3xl">
          <h2 className="text-2xl font-bold tracking-tight sm:text-3xl">
            Extended Strategic Value
          </h2>
          <p className="mt-2 text-muted-foreground">
            Beyond conversion and matching — what this profiling model unlocks for NN.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              title: 'Identify the hidden behavioral drivers',
              text: 'Uncover the 14 psychological and motivational factors that actually drive consultant performance, beyond what traditional KPIs can see.',
            },
            {
              title: 'Build empirically grounded consultant archetypes',
              text: 'Translate behavioral data into 12 distinct microprofiles that represent different ways of being effective, replacing the myth of a single "ideal" consultant.',
            },
            {
              title: 'Improve client satisfaction and retention',
              text: 'Ensure clients are served by consultants whose natural communication style aligns with their needs, reducing friction and strengthening long-term relationships.',
            },
            {
              title: 'Increase consultant engagement and prevent burnout',
              text: 'Identify how each consultant\u2019s intrinsic motivations interact with role demands, enabling better coaching, smarter portfolio allocation, and sustainable performance.',
            },
            {
              title: 'Maximize training ROI',
              text: 'Replace one-size-fits-all development with profile-specific coaching that targets the highest-leverage behavioral gaps per microprofile.',
            },
            {
              title: 'Reduce attrition through early warning signals',
              text: 'Detect motivational misalignment and disengagement patterns before they become resignations, enabling earlier and more targeted intervention.',
            },
            {
              title: 'Make smarter hiring and onboarding decisions',
              text: 'Screen candidates for the 14 behavioral factors that predict conversion, and match new hires to the right mentor, lead types, and targets from day one.',
            },
            {
              title: 'Support flexible agent grouping for campaigns',
              text: 'Enable dynamic consultant groups by microprofile, so that specific campaigns are routed to the behavioral profiles most likely to convert them.',
            },
            {
              title: 'Provide KPI architecture for unified dashboards',
              text: 'Define the behavioral and performance metrics that populate NN\u2019s planned dashboards \u2014 including conversion by microprofile, lead waste per mismatch, burnout risk indicators, and campaign participation by profile type \u2014 going beyond volume metrics to measure matching quality.',
            },
          ].map((item, i) => (
            <div key={i} className="border border-gray-200 bg-white p-5">
              <p className="text-sm font-semibold text-gray-900 mb-1.5">{item.title}</p>
              <p className="text-xs text-gray-600 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============================================================ */}
      {/*  5. CONCLUSION                                                */}
      {/* ============================================================ */}
      <section className="mx-auto max-w-7xl px-6 pb-4">
        <Card className="border-l-4 border-l-[#F17922] bg-gradient-to-br from-[#F17922]/[0.03] to-transparent">
          <CardContent className="py-8 px-8">
            <div className="flex items-start gap-4">
              <div className="shrink-0 mt-1">
                <Target className="h-6 w-6 text-[#F17922]" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-bold tracking-tight text-gray-900">
                  The Bottom Line
                </h2>
                <p className="text-sm text-gray-700 leading-relaxed">
                  This study shows that consultant performance is not random — it follows measurable
                  behavioral patterns. By understanding these patterns, NN can stop treating its sales
                  force as interchangeable and start managing it as what it actually is: a diverse
                  network of specialists, each with a distinct way of creating value.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>
    </div>
  )
}
